import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  extensionZodValidator,
  phoneNumberZodValidor,
} from 'utils/zodValidators'
import { z } from 'zod'
import { ContactCompany } from '../../../models/companies'
import {
  ContactCreateRequest,
  ContactResponse,
  Department,
  Seniority,
} from '../../../models/contacts'
import { handleFormError } from '../../../utils/handleFormError'
import { FbLink } from '../../FbUI/FbLink'
import ContactDetailsStep from './ContactDetailsStep'
import {
  checkDoorSelectionRules,
  SelectDoorsStep,
  selectDoorsStepFormSchema,
} from 'components/Deals/CreateDealModal/SelectDoorsStep'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import FbButton from 'components/FbUI/FbButton'
import { queryClient } from 'services/queryClient'
import useApiService from 'services/api'
import { MobileConfirmationModal } from 'components/MobileConfirmationModal'

export interface ContactDefaultValues extends ContactCreateRequest {
  company_domain?: string
  consent_to_contact: boolean
}

type Props = {
  show: boolean
  onClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: ContactDefaultValues) => Promise<unknown>
  contactToEdit?: ContactResponse
  confirmButtonText?: string
  forCompany?: Partial<ContactCompany>
  initialDoorIds?: number[]
}

export default function MobileContactModal({
  show,
  onClose,
  onSubmitSuccess,
  submitApiAction,
  contactToEdit,
  forCompany,
  initialDoorIds,
}: Props) {
  // Determine if door selection step should be included
  // When editing a contact, always show door selection if there's a company
  // When creating a contact, show door selection if there's a company context
  const includeDoorSelection = !!forCompany || !!contactToEdit
  const [currentStep, setCurrentStep] = useState(1)
  const [shouldValidateDoorSelection, setShouldValidateDoorSelection] =
    useState(false)
  const api = useApiService()

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)

  const newContactFormShape = z
    .object({
      first_name: z.string().refine((v) => v, {
        message: 'Missing first name',
      }),
      last_name: z.string().refine((v) => v, {
        message: 'Missing last name',
      }),
      contact_company: z.object({
        name: z.string(),
        id: z.number(),
        domain: z.string().nullable().optional(),
      }),
      consent_to_contact: z.literal(true).refine((v) => v, {
        message: 'Please confirm you are allowed to contact this person.',
      }),
      company_domain: forCompany
        ? z.any().optional() // Ignore domain for existing companies
        : z.union([
            z.literal(''),
            z
              .string()
              .trim()
              .refine(
                (v) => {
                  if (!v) return true
                  // Match regex for domain https://stackoverflow.com/questions/10306690/what-is-a-regular-expression-which-will-match-a-valid-domain-name-without-a-subd
                  const domainRegex = new RegExp(
                    /^(((?!-))(xn--|_)?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/
                  )
                  // Test if domain is valid and has at least one dot
                  return domainRegex.test(v) && v.includes('.')
                },
                {
                  message: 'Enter domain as: domain.com',
                }
              ),
          ]),
      category_id: z.number().optional(),
      sub_category_id: z.number().optional(),
      email_work: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      email_personal: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      email_generic: z
        .union([z.string().email(), z.literal('')])
        .nullable()
        .optional(),
      url: z.union([
        z.literal(''),
        z.string().trim().url('Enter url as: https://www.domain.com'),
      ]),
      linkedin_url: z
        .union([
          z.literal(''),
          z.null(),
          z
            .string()
            .trim()
            .url('URL should start with: https://www.linkedin.com/')
            .refine(
              (v) => {
                if (!v) {
                  return true
                }

                if (!v.startsWith('https://www.linkedin.com/')) {
                  return false
                }

                return true
              },
              { message: 'URL should start with: https://www.linkedin.com/' }
            ),
        ])
        .nullable()
        .optional(),
      notes: z.string().optional(),
      title: z
        .string()
        .optional()
        .nullable()
        .transform((v) => v || ''),
      tags: z.array(z.number()).optional(),
      extension: extensionZodValidator,
      phone_number: z.union([phoneNumberZodValidor, z.null()]),
      seniority: z.nativeEnum(Seniority).nullable().optional(),
      department: z.nativeEnum(Department).nullable().optional(),
    })
    .partial()
    .merge(selectDoorsStepFormSchema)
    .superRefine((v, ctx) => {
      if (!v) return

      const isNewCompany = v.contact_company?.id === -1

      if (isNewCompany) {
        if (!v.company_domain) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Domain is required to create a new company',
            path: ['company_domain'],
          })
        }
        if (!v.category_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Company category is required for new companies',
            path: ['category_id'],
          })
        }
        if (!v.sub_category_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Company type is required for new companies',
            path: ['sub_category_id'],
          })
        }
      }

      // Ensure at least one email is provided
      if (!v.email_work && !v.email_personal && !v.email_generic) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one email address is required',
          path: ['email_work'],
        })
      }

      // Only validate door selection if we're on step 2 or submitting
      if (shouldValidateDoorSelection) {
        checkDoorSelectionRules(v, ctx)
      }
    })

  const defaultValues = useCallback(() => {
    if (!contactToEdit) {
      return {
        first_name: '',
        last_name: '',
        consent_to_contact: false,
        url: '',
        linkedin_url: '',
        notes: '',
        title: '',
        seniority: undefined,
        department: undefined,
        tags: [],
        contact_company: {} as ContactDefaultValues['contact_company'],
        company_domain: '',
        email_work: '',
        email_personal: '',
        email_generic: '',
        phone_number: '',
        extension: '',
        apply_to:
          initialDoorIds && initialDoorIds.length > 0
            ? 'select_locations'
            : 'entire_company',
        door_ids: initialDoorIds,
      } as ContactDefaultValues
    }

    // When editing, determine apply_to based on whether door_ids exist
    const hasInitialDoorIds = initialDoorIds && initialDoorIds.length > 0
    return {
      ...contactToEdit,
      // Override with door selection fields AFTER spreading contactToEdit
      // to ensure apply_to and door_ids are correctly set based on initialDoorIds
      apply_to: hasInitialDoorIds ? 'select_locations' : 'entire_company',
      door_ids: initialDoorIds,
      // Keep the original contact_company for editing (needed for selectedContactCompanyId)
      contact_company: contactToEdit.contact_company as any,
      company_domain: contactToEdit?.contact_company?.domain,
      tags: contactToEdit?.tags?.map((tag) => tag.id),
    } as ContactDefaultValues
  }, [contactToEdit, initialDoorIds])

  const formMethods = useForm({
    defaultValues: defaultValues(),
    mode: 'onChange',
    resolver: zodResolver(newContactFormShape),
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
  })

  const { handleSubmit, setError, reset, watch } = formMethods

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const onSubmit = handleSubmit(async (values) => {
    // Ensure door selection validation is enabled for final submission
    if (!shouldValidateDoorSelection && includeDoorSelection) {
      setShouldValidateDoorSelection(true)
      // Re-trigger validation
      const isValid = await formMethods.trigger()
      if (!isValid) {
        return
      }
    }

    // Transform only the email fields from empty strings to null
    const transformedEmails = {
      email_work: values.email_work === '' ? null : values.email_work,
      email_personal:
        values.email_personal === '' ? null : values.email_personal,
      email_generic: values.email_generic === '' ? null : values.email_generic,
    }

    // Merge transformed emails with the rest of the form values
    const transformedValues: ContactDefaultValues = {
      ...values,
      ...transformedEmails,
    }

    const [err] = await to(submitApiAction(transformedValues))
    if (err) {
      const keys = Object.keys(err)
      // If the error does not include `door_ids`, or `apply_to`, it should go back to the 1st step
      if (!keys.includes('door_ids') && !keys.includes('apply_to')) {
        setCurrentStep(1)
        setShouldValidateDoorSelection(false)
      }
      handleFormError(err, setError)
      return
    }

    handleClose()
    onSubmitSuccess?.()
  })

  const formContactCompany = watch('contact_company')

  const selectedContactCompanyId = useMemo(() => {
    if (formContactCompany) {
      return formContactCompany.id
    }
    return contactToEdit?.contact_company?.id
  }, [formContactCompany, contactToEdit])

  const { data: { count: companyTotalDoors } = { count: 0 } } =
    api.useGetDoorsForSelectionCount(selectedContactCompanyId ?? 0)

  const handleCancel = useCallback(() => {
    if (currentStep > 1) {
      // Going back to step 1 - disable door selection validation
      if (currentStep === 2) {
        setShouldValidateDoorSelection(false)
        // Trigger validation to clear any door selection errors
        setTimeout(() => {
          formMethods.trigger()
        }, 0)
      }
      setCurrentStep((prev) => prev - 1)
      return
    }
    handleClose()
  }, [currentStep, handleClose, formMethods])

  if (!show) {
    return null
  }

  if (showDeleteConfirmationModal) {
    return (
      <MobileConfirmationModal
        title="Delete this Contact?"
        message="This action cannot be undone."
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={async () => {
          if (contactToEdit) {
            await api.deleteContact(contactToEdit.id)
            handleClose()
            setShowDeleteConfirmationModal(false)
            queryClient.invalidateQueries()
          }
        }}
      />
    )
  }

  return (
    <MobileModal
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Contacts
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Edit Contact</h1>
          <div className="text-xs text-gray-400 mt-2">
            Contacts match to companies based on their domain.
          </div>
        </div>
      }
      footer={
        <div>
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={handleCancel}
              >
                Cancel
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={onSubmit}
              >
                Save
              </FbButton>
            </div>
          </div>
          <div className="flex justify-center">
            <FbLink
              onClick={async (e) => {
                e.preventDefault()
                if (contactToEdit) {
                  setShowDeleteConfirmationModal(true)
                }
              }}
              style={{ fontSize: 10 }}
              className="text-danger"
              to="#"
            >
              Delete Contact
            </FbLink>
          </div>
        </div>
      }
    >
      <FormProvider {...formMethods}>
        {currentStep === 1 && (
          <ContactDetailsStep
            contactToEdit={contactToEdit}
            forCompany={forCompany}
          />
        )}
        {currentStep === 2 && (
          <SelectDoorsStep
            chainProxyId={selectedContactCompanyId!}
            companyTotalDoors={companyTotalDoors}
            radioGroupLabel="Apply this contact to:"
          />
        )}
      </FormProvider>
    </MobileModal>
  )
}
